import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import DeleteRecurrenceModal from './DeleteRecurrenceModal';
import EditRecurrenceModal from './EditRecurrenceModal';


const EventModal = ({ event, defaultDate, whiteboardList = [], onSave, onDelete, onClose, onOpenWhiteboard, prompt, key }) => {
const [title, setTitle] = useState("");
const [description, setDescription] = useState("");
const [allDay, setAllDay] = useState(false);
const [linkedWhiteboards, setLinkedWhiteboards] = useState([]);
const [visibility, setVisibility] = useState('shared');
const [color, setColor] = useState("#3788d8");
const [reminderMinutes, setReminderMinutes] = useState(60);
const { t } = useTranslation();
const [errors, setErrors] = useState({});

const [freq, setFreq] = useState('none');
const [recurrenceInterval, setRecurrenceInterval] = useState(1);
const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
const [daysOfWeek, setDaysOfWeek] = useState([]);
const [showEditModal, setShowEditModal] = useState(false);
const [showDeleteModal, setShowDeleteModal] = useState(false);

const iconBlueStyle = {
  fontSize: '22px',
  color: '#007bff',
};

const iconRedStyle = {
  fontSize: '22px',
  color: 'red',
};


// Résumé texte de la récurrence (affiché en dur sous les inputs)
const recurrenceSummary = () => {
  if (!freq || freq === 'none') return null;
  const freqLabel = { daily: t('agenda.days'), weekly: t('agenda.weeks'), monthly: t('agenda.months') }[freq];
  let summary = `🔁 ${t('agenda.every')} ${recurrenceInterval} ${freqLabel}`;
  if (freq === 'weekly' && daysOfWeek.length > 0) {
    const dayNames = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];
    summary += ` (${daysOfWeek.map(d => dayNames[d]).join(', ')})`;
  }
  if (recurrenceEndDate) summary += ` — ${t('agenda.until')} ${new Date(recurrenceEndDate).toLocaleDateString(i18n.language)}`;
  return summary;
};

const displayedColor =
  visibility === 'private'
    ? '#888'
    : color;

  const handleAddWhiteboard = (e) => {
    const id = e.target.value;
    if (!id || linkedWhiteboards.find((w) => w.whiteboardId === id)) return;
    const wb = whiteboardList.find((w) => w._id === id);
    if (wb) {
      setLinkedWhiteboards((prev) => [...prev, { whiteboardId: wb._id, name: wb.name }]);
    }
    e.target.value = "";
  };

  const handleRemoveWhiteboard = (id) => {
    setLinkedWhiteboards((prev) => prev.filter((w) => w.whiteboardId !== id));
  };

// UTC → format datetime-local (input HTML)
const toLocalDatetimeInput = (dateStr) => {
  if (!dateStr) return "";
if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return `${dateStr}T00:00`;
  }
  const date = new Date(dateStr);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date - offset).toISOString().slice(0, 16);
};

const [start, setStart] = useState(
  event?.start ? toLocalDatetimeInput(event.start) : 
  defaultDate ? toLocalDatetimeInput(defaultDate) : ""
);
const [end, setEnd] = useState(
  event?.end ? toLocalDatetimeInput(event.end) : 
  defaultDate ? toLocalDatetimeInput(defaultDate) : ""
);
const now = new Date();

// Date locale → UTC pour envoi au backend
const toUTC = (localDateStr) => {
  if (!localDateStr) return "";
  return new Date(localDateStr).toISOString();
};

// Minuit en local
const todayMidnight = () => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return toLocalDatetimeInput(now);
};

useEffect(() => {
  if (!event && freq === 'weekly' && defaultDate) {
    const dayOfWeek = new Date(defaultDate).getDay();
    setDaysOfWeek([dayOfWeek]);
  }
}, [defaultDate, freq, event]);

useEffect(() => {
  if (event) {
    setTitle(event.title || "");
    setDescription(event.description || "");
    setColor(event.color || "#3788d8");
    setAllDay(event.allDay || false);
    setLinkedWhiteboards(event.whiteboards || []);
    setVisibility(event.visibility || 'shared');
    setReminderMinutes(event.reminderMinutes ?? 60);
    setStart(event.start ? toLocalDatetimeInput(event.start) : "");
    setEnd(event.end ? toLocalDatetimeInput(event.end) : "");
    setFreq(event.recurrence?.freq || 'none');
    setRecurrenceInterval(event.recurrence?.interval || 1);
    setRecurrenceEndDate(
      event.recurrence?.endDate
        ? event.recurrence.endDate.slice(0, 10)  // "2025-12-15"
        : ''
    );
    setDaysOfWeek(event.recurrence?.daysOfWeek || []);
  } else {
    // ← nouveau événement
    setTitle("");
    setDescription("");
    setColor("#3788d8");
    setAllDay(false);
    setLinkedWhiteboards([]);
    setVisibility('shared');
    setReminderMinutes(60);
    setStart(defaultDate ? toLocalDatetimeInput(defaultDate) : "");
    setEnd(defaultDate ? toLocalDatetimeInput(defaultDate) : "");
    setFreq('none');
    setRecurrenceInterval(1);
    setRecurrenceEndDate('');
    setDaysOfWeek([]);
  }
}, [event]); 

  const handleSave = async() => {
  const newErrors = {};
  if (!title.trim()) newErrors.title = t('agenda.error_title');
  const startDate = new Date(start);
  const endDate   = new Date(end || start);
  if (startDate < now)       newErrors.start = t('agenda.error_past');
  if (endDate < startDate)   newErrors.end   = t('agenda.error_end_before_start');
  if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
  setErrors({});


// Vérification cohérence jours hebdomadaires
if (freq === 'weekly' && daysOfWeek.length > 0) {
  const startDayOfWeek = new Date(start).getDay(); // 0=dim … 6=sam
  if (!daysOfWeek.includes(startDayOfWeek)) {
    const dayNames = {
      fr: ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi'],
      en: ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'],
      es: ['domingo','lunes','martes','miércoles','jueves','viernes','sábado'],
      de: ['Sonntag','Montag','Dienstag','Mittwoch','Donnerstag','Freitag','Samstag'],
      it: ['domenica','lunedì','martedì','mercoledì','giovedì','venerdì','sabato'],
      pt: ['domingo','segunda','terça','quarta','quinta','sexta','sábado'],
      ar: ['الأحد','الاثنين','الثلاثاء','الأربعاء','الخميس','الجمعة','السبت'],
      el: ['Κυριακή','Δευτέρα','Τρίτη','Τετάρτη','Πέμπτη','Παρασκευή','Σάββατο'],
      uk: ['неділя','понеділок','вівторок','середа','четвер','пʼятниця','субота'],
	nl: ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'],
	sv: ['söndag', 'måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag'],
	da: ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'],
	no: ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'],
	is: ['sunnudagur', 'mánudagur', 'þriðjudagur', 'miðvikudagur', 'fimmtudagur', 'föstudagur', 'laugardagur'],
	fi: ['sunnuntai', 'maanantai', 'tiistai', 'keskiviikko', 'torstai', 'perjantai', 'lauantai'],
	ro: ['duminică', 'luni', 'marți', 'miercuri', 'joi', 'vineri', 'sâmbătă'],
	pl: ['niedziela', 'poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota'],
	sl: ['nedelja', 'ponedeljek', 'torek', 'sreda', 'četrtek', 'petek', 'sobota'],
	cs: ['neděle', 'pondělí', 'úterý', 'středa', 'čtvrtek', 'pátek', 'sobota'],
	sk: ['nedeľa', 'pondelok', 'utorok', 'streda', 'štvrtok', 'piatok', 'sobota'],
	hr: ['nedjelja', 'ponedjeljak', 'utorak', 'srijeda', 'četvrtak', 'petak', 'subota'],
	hu: ['vasárnap', 'hétfő', 'kedd', 'szerda', 'csütörtök', 'péntek', 'szombat'],
	lt: ['sekmadienis', 'pirmadienis', 'antradienis', 'trečiadienis', 'ketvirtadienis', 'penktadienis', 'šeštadienis'],
	lv: ['svētdiena', 'pirmdiena', 'otrdiena', 'trešdiena', 'ceturdiena', 'piekdiena', 'sestdiena'],
	et: ['pühapäev', 'esmaspäev', 'teisipäev', 'kolmapäev', 'neljapäev', 'reede', 'laupäev'],
	bg: ['неделя', 'понеделник', 'вторник', 'сряда', 'четвъртък', 'петък', 'събота'],
	sr: ['недеља', 'понедељак', 'уторак', 'среда', 'четвртак', 'петак', 'субота']
    };
    const names = dayNames[i18n.language] || dayNames.fr;
    const startDayName = names[startDayOfWeek];
    const selectedDayNames = daysOfWeek.map(d => names[d]).join(', ');

    await prompt.alert(
      t('agenda.error_day_mismatch', {
        startDay: startDayName,
        selectedDays: selectedDayNames,
      })
    );
    return; // ← bloque l'enregistrement
  }
}

// Si c'est une occurrence récurrente → demander le périmètre
  const isOccurrence = event?.recurrence?.freq !== 'none' || event?.id?.includes('_');
  if (event && isOccurrence) {
    setShowEditModal(true); // ← ouvre le choix avant d'envoyer
    return;
  }
  submitSave('all'); // événement simple ou nouvelle création
};

const submitSave = (mode) => {
  onSave({
    id: event?._id || event?.id,
    title, description,
    start: toUTC(start),
    end:   toUTC(end || start),
    color, allDay,
    whiteboards: linkedWhiteboards,
    visibility,
    reminderMinutes: reminderMinutes ?? null,
    notificationSent: false,
    recurrence: { freq, interval: recurrenceInterval, endDate: recurrenceEndDate ? toUTC(recurrenceEndDate) : null, daysOfWeek: freq === 'weekly' ? daysOfWeek : [] },
    mode, // ← transmis au parent pour le PUT ?mode=
  });
};


  return (

    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 6000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', 
overflowY: 'auto',
  paddingTop: 70,    /* hauteur de ton header fixe */
  paddingBottom: 20,
    }}>
      <div style={{
        background: 'var(--bg-color, #fff)', borderRadius: 12,
        padding: 24, width: '100%', maxWidth: 480,
        display: 'flex', flexDirection: 'column', gap: 12, 
height: '85dvh',        /* ne dépasse jamais 85% de la hauteur écran */
  overflowY: 'auto',        /* scroll interne si contenu trop long */
  marginTop: 'auto',        /* pousse vers le centre */
  marginBottom: 'auto',
      }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, 
 }}>
  
  {/* Titre */}
  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text-color)' }}>
    {event ? t('agenda.modifevent') : t('agenda.newevent')}
  </h3>

  {/* Boutons droite */}
  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
    
    {/* Sauvegarder */}
    <button onClick={handleSave} style={{
      background: 'none', border: 'none', cursor: 'pointer', fontSize: 18,
    }} title={t('agenda.save')}>
      <i className="ti ti-device-floppy" style={iconBlueStyle} aria-hidden="true" />
    </button>

    {/* Supprimer — seulement si édition */}
    {event && (
      <button 
onClick={() => {
      if (!recurrenceSummary()) {
        onDelete(event.id);
      } else {
        setShowDeleteModal(true);
      }
    }}
style={{
        background: 'none', border: 'none', cursor: 'pointer', fontSize: 18,
      }} title={t('agenda.sup')}>
        <i className="ti ti-trash" style={iconRedStyle} aria-hidden="true" />
      </button>
    )}


    {/* Fermer */}
    <button onClick={onClose} style={{
      background: 'none', border: 'none', fontSize: 18,
      cursor: 'pointer', color: '#f44336',
    }}>
      <i className="ti ti-circle-x" style={iconRedStyle} aria-hidden="true" />
    </button>

  </div>
</div>

{/* Toggle visibilité */}
<div style={{ display: 'flex', gap: 8 }}>
  <button
    onClick={() => setVisibility('shared')}
    style={{
      flex: 1,
      padding: '8px',
      color: 'var(--text-color)',
      borderRadius: 6,
      border: `2px solid ${visibility === 'shared' ? '#3788d8' : '#ccc'}`,
      background: visibility === 'shared' ? 'var(--input-bg)' : 'none',
      cursor: 'pointer',
      fontWeight: visibility === 'shared' ? 'bold' : 'normal',
    }}
  >
    🌐 {t('agenda.shared')}
  </button>
  <button
    onClick={() => setVisibility('private')}
    style={{
      flex: 1,
      padding: '8px',
      color: 'var(--text-color)',
      borderRadius: 6,
      border: `2px solid ${visibility === 'private' ? '#888' : '#ccc'}`,
      background: visibility === 'private' ? 'var(--input-bg)' : 'none',
      cursor: 'pointer',
      fontWeight: visibility === 'private' ? 'bold' : 'normal',
    }}
  >
    🔒 {t('agenda.private')}
  </button>
</div>
        <input type="text"
placeholder={t('agenda.title')} 
value={title} 
onChange={(e) => {
      setTitle(e.target.value);

      if (errors.title) {
        setErrors(prev => ({ ...prev, title: null }));
      }
    }}

     
          style={{ padding: '8px', borderRadius: 6, border: '1px solid #ccc' }} required />
{errors.title && (
    <div style={{
      color: '#f44336',
      fontSize: 12,
      marginTop: 0,
    }}>
      {errors.title}
    </div>
  )}
        <textarea
  placeholder={t('agenda.description')}
  value={description}
  onChange={(e) => setDescription(e.target.value)}
  rows={3}
  style={{
    padding: '8px',
    borderRadius: 6,
    border: '1px solid #ccc',
    resize: 'vertical',
background: 'var(--input-bg)',
color: 'var(--text-color)',
    minHeight: 100,
    flexShrink: 0, // ← important sur mobile
  }}
/>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={allDay} 
onChange={(e) => {
    const checked = e.target.checked;
    setAllDay(checked);

    if (checked) {
      // 🔥 on normalise les dates en "jour entier"

      const normalizeToDayStart = (d) => {
        if (!d) return "";
        const date = new Date(d);
        date.setHours(0, 0, 0, 0);
        return toLocalDatetimeInput(date);
      };

      const normalizeToDayEnd = (d) => {
        if (!d) return "";
        const date = new Date(d);
        date.setHours(23, 59, 0, 0);
        return toLocalDatetimeInput(date);
      };

      setStart((prev) => normalizeToDayStart(prev));
      setEnd((prev) => normalizeToDayEnd(prev));
if (errors.end) {
        setErrors(prev => ({ ...prev, end: null }));
      }

    }
  }} 
/>
          {t('agenda.allday')}
        </label>

        <div style={{
  display: 'flex',
  flexDirection: 'column', // ← colonne au lieu de row
  gap: '8px',
}}>
  <div>
    <label style={{ fontSize: 12, color: 'var(--text-color)' }}>{t('agenda.debut')}</label>
    <input
      type={allDay ? "date" : "datetime-local"}
      value={start}
      onChange={(e) => {
        setStart(e.target.value);
        if (errors.start) setErrors(prev => ({ ...prev, start: null }));
      }}
      style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #ccc', boxSizing: 'border-box' }}
    />
    {errors.start && (
      <div style={{ color: '#f44336', fontSize: 12, marginTop: 4 }}>
        {errors.start}
      </div>
    )}
  </div>

  <div>
    <label style={{ fontSize: 12, color: 'var(--text-color)' }}>{t('agenda.fin')}</label>
    <input
      type={allDay ? "date" : "datetime-local"}
      value={end}
      onChange={(e) => {
        setEnd(e.target.value);
        if (errors.end) setErrors(prev => ({ ...prev, end: null }));
      }}
      style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #ccc', boxSizing: 'border-box' }}
    />
    {errors.end && (
      <div style={{ color: '#f44336', fontSize: 12, marginTop: 4 }}>
        {errors.end}
      </div>
    )}
  </div>
</div>
<div style={{ display: 'flex', gap: '10px' }}>
{/* Résumé récurrence en lecture (mode édition) */}
{event && recurrenceSummary() && (
  <div style={{
    fontSize: 13, color: '#3788d8', background: '#e8f0fe',
    borderRadius: 8, padding: '8px 12px',
  }}>
    {recurrenceSummary()}
  </div>
)}
</div>

{/* Modal choix périmètre de modification */}
{showEditModal && (
  <EditRecurrenceModal
    onChoice={(mode) => { setShowEditModal(false); submitSave(mode); }}
    onClose={() => setShowEditModal(false)}
  />
)}

<div style={{ borderTop: '1px solid #eee', paddingTop: 12 }}>
  <label style={{ fontSize: 12, color: 'var(--text-color)', display: 'block', marginBottom: 4 }}>
    🔁 {t('agenda.recurrence')}
  </label>

  <select value={freq} onChange={e => setFreq(e.target.value)}
    style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #ccc' }}>
    <option value="none">{t('agenda.no_recurrence')}</option>
    <option value="daily">{t('agenda.daily')}</option>
    <option value="weekly">{t('agenda.weekly')}</option>
    <option value="monthly">{t('agenda.monthly')}</option>
  </select>

  {freq !== 'none' && (
    <>
      {/* Intervalle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
        <label style={{ fontSize: 12, color: 'var(--text-color)' }}>
          {t('agenda.every')}
        </label>
        <input type="number" min={1} max={30} value={recurrenceInterval}
          onChange={e => setRecurrenceInterval(parseInt(e.target.value))}
          style={{ width: 60, padding: '6px', borderRadius: 6, border: '1px solid #ccc' }} />
        <span style={{ fontSize: 12 }}>
          {freq === 'daily' ? t('agenda.days') : freq === 'weekly' ? t('agenda.weeks') : t('agenda.months')}
        </span>
      </div>

      {/* Jours de la semaine (weekly uniquement) */}
      {freq === 'weekly' && (
  <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
    {(() => {
      // Générer les initiales des jours selon la locale courante
      const baseDate = new Date(2024, 0, 7); // dimanche 7 janvier 2024
      return Array.from({ length: 7 }, (_, i) => {
        const date = new Date(baseDate);
        date.setDate(baseDate.getDate() + i);
        const label = new Intl.DateTimeFormat(i18n.language, { weekday: 'narrow' }).format(date);
        return (
          <button key={i}
            onClick={() => setDaysOfWeek(prev =>
              prev.includes(i) ? prev.filter(d => d !== i) : [...prev, i]
            )}
            style={{
              width: 32, height: 32, borderRadius: '50%', border: '1px solid #ccc',
              background: daysOfWeek.includes(i) ? '#3788d8' : 'none',
              color: daysOfWeek.includes(i) ? '#fff' : 'var(--text-color)',
              cursor: 'pointer', fontWeight: 'bold', fontSize: 12,
            }}>
            {label}
          </button>
        );
      });
    })()}
  </div>
)}

      {/* Date de fin */}
      <div style={{ marginTop: 8 }}>
        <label style={{ fontSize: 12, color: 'var(--text-color)' }}>
          {t('agenda.recurrence_end')}
        </label>
        <input type="date" value={recurrenceEndDate}
          onChange={e => setRecurrenceEndDate(e.target.value)}
          style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} />
      </div>
    </>
  )}
</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, color: 'var(--text-color)' }}>{t('agenda.couleur')}</label>
          <input type="color" value={displayedColor} disabled={visibility === 'private'} onChange={(e) => setColor(e.target.value)}
            style={{
      width: 40,
      height: 32,
      border: 'none',
      cursor: visibility === 'private'
        ? 'not-allowed'
        : 'pointer',
      opacity: visibility === 'private'
        ? 0.6
        : 1
    }} />
        </div>

        {/* Tableaux liés */}
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-color)', display: 'block', marginBottom: 4 }}>
            ✍️ {t('agenda.tableaux')}
          </label>
          <select onChange={handleAddWhiteboard} style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #ccc' }}>
            <option value="">{t('agenda.addtableau')}</option>
            {whiteboardList.map((wb) => (
              <option key={wb._id} value={wb._id}>{wb.name}</option>
            ))}
          </select>
          {linkedWhiteboards.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {linkedWhiteboards.map((wb) => (
                <div key={wb.whiteboardId} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '4px 8px', background: '#f0f0f0', borderRadius: 6
                }}>
                   {/* ← lien cliquable */}
    <span
      onClick={() => onOpenWhiteboard(wb.whiteboardId)}
      style={{ fontSize: 13, cursor: 'pointer', color: '#3788d8', textDecoration: 'underline' }}
    >
      📝 {wb.name}
    </span>
                  <button onClick={() => handleRemoveWhiteboard(wb.whiteboardId)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'red' }}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>

{/* Sélecteur de rappel */}
<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
  <span style={{ fontSize: 14 }}>🔔 {t('agenda.notif')}</span>
  <select
    value={reminderMinutes}
    onChange={(e) => {
  const val = e.target.value;
  setReminderMinutes(val === '' ? null : parseInt(val)); // ← null si pas d'alerte
}}
    style={{ padding: '8px', borderRadius: 6, border: '1px solid #ccc', flex: 1 }}
  >
    <option value=''>{t('agenda.non_alerte')}</option>
    <option value={15}>{t('agenda.15')}</option>
    <option value={30}>{t('agenda.30')}</option>
    <option value={60}>{t('agenda.une_heure')}</option>
    <option value={120}>{t('agenda.deux_heures')}</option>
    <option value={1440}>{t('agenda.24')}</option>
  </select>
</div>

        {/* Boutons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
    
    {/* Sauvegarder */}
    <button onClick={handleSave} style={{
      background: 'none', border: 'none', cursor: 'pointer', fontSize: 18,
    }} title={t('agenda.save')}>
      <i className="ti ti-device-floppy" style={iconBlueStyle} aria-hidden="true" />

    </button>

    {/* Supprimer — seulement si édition */}
    {event && (
      <button 
onClick={() => {
      if (!recurrenceSummary()) {
        onDelete(event.id);
      } else {
        setShowDeleteModal(true);
      }
    }}
 

style={{
        background: 'none', border: 'none', cursor: 'pointer', fontSize: 18,
      }} title={t('agenda.sup')}>
        <i className="ti ti-trash" style={iconRedStyle} aria-hidden="true" />
      </button>
    )}



    {/* Fermer */}
    <button onClick={onClose} style={{
      background: 'none', border: 'none', fontSize: 18,
      cursor: 'pointer', color: '#f44336',
    }}>
      <i className="ti ti-circle-x" style={iconRedStyle} aria-hidden="true" />
    </button>

 

      </div>
</div>

{showDeleteModal && (
  <DeleteRecurrenceModal
    isRecurring={!!event?.recurrence && event.recurrence.freq !== 'none'}
    onChoice={(mode) => {
      setShowDeleteModal(false);
      onDelete(event.id, mode); // ← passer le mode au parent
    }}
    onClose={() => setShowDeleteModal(false)}
  />
)}
    </div>

  );
};

export default EventModal;